import React from 'react';
import clsx from 'clsx';
import * as SliderPrimitive from '@radix-ui/react-slider';
import {
  SliderProps,
  sliderVariantsType,
  sliderSizesType,
} from '@src/interfaces/components/coreUI/Slider';
import { formatCurrency } from '@src/utils/helper';

const Slider = ({
  variant = 'pink',
  size = 'md',
  min = 0,
  max = 0,
  value,
  onChange,
  isMulti = false,
  minDistance = 0,
  extraClasses = '',
  isVisibleMinMax = false,
  isFormatCurrency = false,
  sliderProps = {},
  maxExtensible = false,
}: SliderProps) => {
  const variants: sliderVariantsType = {
    pink: 'bg-pink-dark',
    blue: 'bg-blue-dark',
  };

  const lightVariants: sliderVariantsType = {
    pink: 'bg-gray-light',
    blue: 'bg-gray-light',
  };

  const sizes: sliderSizesType = {
    md: 'w-10 h-10',
    sm: 'w-6 h-6',
  };

  const rootPaddingBySize: sliderSizesType = {
    md: 'px-3',
    sm: 'px-2',
  };

  const trackSizes: sliderSizesType = {
    md: 'h-[10px] my-1.5',
    sm: 'h-[7px] my-1',
  };

  const thumbClasses = clsx(
    'rounded-full border border-gray-lighter !text-white bg-white focus-visible:outline-none z-20 shadow-lg cursor-pointer block',
    variants[variant as keyof sliderVariantsType],
    sizes[size as keyof sliderSizesType]
  );

  const trackClasses = clsx(
    'relative w-full rounded-lg',
    lightVariants[variant as keyof sliderVariantsType],
    trackSizes[size as keyof sliderSizesType]
  );

  const rangeClasses = clsx(
    'absolute rounded-lg pointer-events-none h-full',
    variants[variant as keyof sliderVariantsType]
  );

  const coercedValue: number[] = Array.isArray(value)
    ? (value as number[])
    : [value as number];

  const handleChange = (val: number[]) => {
    if (isMulti) {
      onChange(val);
    } else {
      onChange(val[0] ?? min);
    }
  };

  const step = (sliderProps as { step?: number })?.step ?? 1;
  const minStepsBetweenThumbs = Math.max(0, Math.floor(minDistance / step));
  const thumbCount = coercedValue.length > 0 ? coercedValue.length : 1;

  return (
    <div
      className={clsx(
        'w-full',
        rootPaddingBySize[size as keyof sliderSizesType],
        extraClasses
      )}
    >
      <SliderPrimitive.Root
        min={min}
        max={max}
        value={coercedValue}
        onValueChange={handleChange}
        minStepsBetweenThumbs={
          coercedValue.length > 1 ? minStepsBetweenThumbs : undefined
        }
        step={step}
        aria-label="Slider"
        className="relative flex w-full select-none touch-none items-center cursor-pointer"
        {...(sliderProps as Record<string, unknown>)}
      >
        <SliderPrimitive.Track className={trackClasses}>
          <SliderPrimitive.Range className={rangeClasses} />
        </SliderPrimitive.Track>
        {Array.from({ length: thumbCount }).map((_, index) => (
          <SliderPrimitive.Thumb key={index} className={thumbClasses} />
        ))}
      </SliderPrimitive.Root>

      {isVisibleMinMax ? (
        <div className="flex justify-between text-sm text-black-dark pt-6 font-medium">
          <p>{!isFormatCurrency ? min : formatCurrency(min || 0)}</p>
          <p>
            {!isFormatCurrency ? max : formatCurrency(max || 0, true)}
            {maxExtensible ? '+' : ''}
          </p>
        </div>
      ) : null}
    </div>
  );
};

export default Slider;
